/**
 * telegram-dev-mock.ts
 * Purpose: Injects a fake window.Telegram.WebApp with HMAC-signed initData
 * during Vite dev mode only. Dead-code-eliminated in production builds
 * because all code is behind `if (!import.meta.env.DEV) return`.
 *
 * Usage: await installDevMock() before initTelegramApp() in main.tsx.
 * Set VITE_DEV_BOT_TOKEN in .env.local to enable signing.
 */

function bufferToHex(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Computes HMAC-SHA256 signed initData string matching Telegram's backend
 * verification algorithm.
 *
 * Steps:
 *   secretKey = HMAC-SHA256("WebAppData", botToken)
 *   dataCheckString = sorted keys joined as "key=value\n" (no trailing newline)
 *   hash = HMAC-SHA256(secretKey, dataCheckString) → hex
 *   returns URLSearchParams({ ...params, hash }).toString()
 */
export async function signInitData(
  params: Record<string, string>,
  botToken: string,
): Promise<string> {
  const enc = new TextEncoder();

  // Step 1: secretKey = HMAC-SHA256("WebAppData", botToken)
  const hmacKeyMaterial = await crypto.subtle.importKey(
    'raw',
    enc.encode('WebAppData'),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const secretKeyBuffer = await crypto.subtle.sign(
    'HMAC',
    hmacKeyMaterial,
    enc.encode(botToken),
  );
  const secretKey = await crypto.subtle.importKey(
    'raw',
    secretKeyBuffer,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );

  // Step 2: dataCheckString = sorted keys, "key=value\n" joined (no trailing newline)
  const dataCheckString = Object.keys(params)
    .sort()
    .map((key) => `${key}=${params[key]}`)
    .join('\n');

  // Step 3: hash = HMAC-SHA256(secretKey, dataCheckString) → hex
  const hashBuffer = await crypto.subtle.sign(
    'HMAC',
    secretKey,
    enc.encode(dataCheckString),
  );
  const hash = bufferToHex(hashBuffer);

  // Step 4: return URLSearchParams with hash appended
  const urlParams = new URLSearchParams({ ...params, hash });
  return urlParams.toString();
}

/**
 * Installs a minimal Telegram.WebApp mock in the browser window.
 * No-op in production (import.meta.env.DEV === false → dead code eliminated).
 */
export async function installDevMock(): Promise<void> {
  // Guard: no-op in production builds (Vite DCE strips this entire block)
  if (!import.meta.env.DEV) return;

  // Guard: SSR / non-browser
  if (typeof window === 'undefined') return;

  // Guard: real Telegram environment already present (has non-empty initData).
  // The telegram-web-app.js script always creates an empty WebApp shell in browsers,
  // so checking presence alone misclassifies dev as prod — check initData instead.
  const win = window as unknown as Record<string, unknown>;
  const existingWebApp = (win.Telegram as { WebApp?: { initData?: string } } | undefined)?.WebApp;
  if (existingWebApp?.initData) {
    return;
  }

  const botToken = import.meta.env.VITE_DEV_BOT_TOKEN as string | undefined;
  if (!botToken) {
    console.warn('[dev-mock] VITE_DEV_BOT_TOKEN not set; skipping mock');
    return;
  }

  const authDate = Math.floor(Date.now() / 1000);
  const user = {
    id: 698259360,
    first_name: 'Dev',
    last_name: 'User',
    username: 'devuser',
    language_code: 'en',
    is_premium: false,
  };

  const params: Record<string, string> = {
    auth_date: String(authDate),
    user: JSON.stringify(user),
    query_id: 'dev-query-id',
  };

  const signedInitData = await signInitData(params, botToken);

  // Extract hash from signed string for initDataUnsafe
  const parsedSigned = new URLSearchParams(signedInitData);
  const extractedHash = parsedSigned.get('hash') ?? '';

  // Build chainable no-op MainButton
  const MainButton = {
    text: '',
    color: '',
    textColor: '',
    isVisible: false,
    isActive: true,
    isProgressVisible: false,
    setText: () => MainButton,
    onClick: () => MainButton,
    offClick: () => MainButton,
    show: () => MainButton,
    hide: () => MainButton,
    enable: () => MainButton,
    disable: () => MainButton,
    showProgress: () => MainButton,
    hideProgress: () => MainButton,
    setParams: () => MainButton,
  };

  // Build chainable no-op BackButton
  const BackButton = {
    isVisible: false,
    onClick: () => BackButton,
    offClick: () => BackButton,
    show: () => BackButton,
    hide: () => BackButton,
  };

  // Build chainable no-op HapticFeedback
  const HapticFeedback = {
    impactOccurred: () => HapticFeedback,
    notificationOccurred: () => HapticFeedback,
    selectionChanged: () => HapticFeedback,
  };

  const WebApp = {
    initData: signedInitData,
    initDataUnsafe: {
      user,
      auth_date: authDate,
      query_id: 'dev-query-id',
      hash: extractedHash,
    },
    version: '7.0',
    platform: 'web',
    colorScheme: 'dark' as const,
    themeParams: {
      bg_color: '#0a0613',
      text_color: '#f5e6d3',
      hint_color: '#9a8d7a',
      link_color: '#d4af37',
      button_color: '#d4af37',
      button_text_color: '#0a0613',
      secondary_bg_color: '#1a1426',
    },
    isExpanded: true,
    viewportHeight: 800,
    viewportStableHeight: 800,
    // Bot API 8.0 safe-area insets — zero in the browser dev mock (no chrome).
    safeAreaInset: { top: 0, right: 0, bottom: 0, left: 0 },
    contentSafeAreaInset: { top: 0, right: 0, bottom: 0, left: 0 },
    ready: () => {},
    expand: () => {},
    close: () => {},
    enableClosingConfirmation: () => {},
    disableClosingConfirmation: () => {},
    setHeaderColor: () => {},
    setBackgroundColor: () => {},
    setBottomBarColor: () => {},
    onEvent: () => {},
    offEvent: () => {},
    sendData: () => {},
    MainButton,
    BackButton,
    HapticFeedback,
  };

  (window as unknown as Record<string, unknown>).Telegram = { WebApp };

  console.log(
    '[dev-mock] Telegram.WebApp mock installed with signed initData for user 698259360',
  );
}
